import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Copy, Lock, Globe, Check, X } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  getSharedWithTemplates,
  setPermissionType,
  shareTemplate,
} from "@/utils/Services";
import { ITemplate } from "@/utils/Types";
import { toast } from "react-toastify";

interface ShareTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onShare: (emails: string[], permission: string) => void;
  template: ITemplate;
}

const APP_URL = import.meta.env.VITE_APP_URL as string;

export default function ShareTemplateModal({
  open,
  onClose,
  onShare,
  template,
}: ShareTemplateModalProps) {
  const [emails, setEmails] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [permission, setPermission] = useState("restricted");
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const handleAddEmail = () => {
    if (!input) return;

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)) {
      setError("Invalid email address");
      return;
    }
    if (emails.includes(input)) {
      setError("Email already added");
      return;
    }
    setEmails([...emails, input]);
    setInput("");
    setError("");
  };

  const handleRemoveEmail = (email: string) => {
    setEmails(emails.filter((e) => e !== email));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddEmail();
    } else if (e.key === "Backspace" && !input && emails.length > 0) {
      handleRemoveEmail(emails[emails.length - 1]);
    }
  };

  const handleShare = async () => {
    const response = await shareTemplate(Number(template.id), {
      emails,
      permission_type: permission,
    });
    onShare(emails, permission);
    setEmails([]);
    setInput("");
    setError("");
    onClose();
  };

  const fetchSharedWithTemplates = async () => {
    const response = await getSharedWithTemplates({
      template_id: Number(template.id),
    });

    setEmails(response.shared_with);
    setPermission(response.permission_type);
  };

  useEffect(() => {
    if (!open) return;
    setFirstLoad(true);
    fetchSharedWithTemplates();
  }, [open]);

  const handleSetPermissionType = async () => {
    if (firstLoad) {
      setFirstLoad(false);
      return;
    }
    await setPermissionType(template.id!, permission);
    toast.success("Permission type updated successfully");
  };

  useEffect(() => {
    if (!open) return;
    handleSetPermissionType();
  }, [permission]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `${APP_URL}/dashboard/shared-templates/${template?.public_access_key}`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle className="text-xl">Share Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block mb-2 text-sm">
              Enter email addresses to share with:
            </label>
            <div className="relative">
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg min-h-[40px] bg-background overflow-x-auto max-h-[100px]">
                <div className="flex flex-wrap gap-1 min-w-full">
                  {emails.map((email) => (
                    <span
                      key={email}
                      className="bg-gray-25 dark:bg-gray-800 px-2 py-1 rounded-full flex items-center text-sm whitespace-nowrap"
                    >
                      {email}
                      <button
                        type="button"
                        className="ml-1 text-red-500 hover:text-red-600"
                        onClick={() => handleRemoveEmail(email)}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  <input
                    ref={inputRef}
                    type="email"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleAddEmail}
                    placeholder={emails.length === 0 ? "user@example.com" : ""}
                    className="flex-1 min-w-[120px] outline-none bg-transparent dark:text-white"
                  />
                </div>
              </div>
              {error && (
                <div className="text-red-500 text-xs mt-1">{error}</div>
              )}
            </div>
          </div>

          <div>
            <div className="block mb-2 text-sm">General access</div>
            <div className="flex items-center gap-3">
              <div className="relative w-full">
                <Select value={permission} onValueChange={setPermission}>
                  <SelectTrigger className="w-full h-14 border rounded-lg">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        {permission === "restricted" ? (
                          <Lock size={20} weight="fill" />
                        ) : (
                          <Globe size={20} weight="fill" />
                        )}
                        <span className="font-medium text-left">
                          {permission === "restricted"
                            ? "Restricted"
                            : "Anyone with the link"}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-white pl-7">
                        {permission === "restricted"
                          ? "Only people with access can view/use this template"
                          : "Anyone with the link can view/import this template"}
                      </div>
                    </div>
                  </SelectTrigger>
                  <SelectContent className="dark:bg-gray-800">
                    <SelectItem value="restricted">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <Lock size={16} weight="fill" />
                          Restricted
                        </div>
                        <div className="text-xs text-gray-500 dark:text-white pl-6">
                          Only people with access can view/use this template
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="anyone_with_link">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <Globe size={16} weight="fill" />
                          Anyone with the link
                        </div>
                        <div className="text-xs text-gray-500 dark:text-white pl-6">
                          Anyone with the link can view/import this template
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center mt-4">
              <Button
                variant="outline"
                className={`flex items-center gap-1 border rounded-full px-5 py-2 text-base font-medium transition-colors duration-300 min-w-[140px] justify-center ${
                  copied ? "text-green-600 border-green-600" : ""
                }`}
                onClick={handleCopyLink}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                <span className="text-center">
                  {copied ? "Copied" : "Copy link"}
                </span>
              </Button>
              <div className="flex-1" />
              <Button
                className="bg-sky-200 hover:bg-sky-100 text-white rounded-full px-8 py-2 text-base font-semibold"
                onClick={handleShare}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
